/**
 * Shared interaction-state fragments for inputs/* meta-components.
 *
 * These exist for states that the shadcn primitives either don't provide
 * (e.g. a 2px solid focus ring) or that meta-components need to compose on
 * non-shadcn elements (custom cards, radio tiles, etc.).
 *
 * Hover and active text colours are handled at the primitive level
 * (ui/tabs.tsx, ui/sidebar.tsx) using the branded tokens `primary-alt`
 * (hover) and `primary` (active). Those tokens are registered in
 * tailwind.config.js so tailwind-merge can deduplicate them.
 */

/** 2px solid focus ring using `--ring`. */
export const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-ring';

/** Selected/active state styling. Apply alongside a resting `border border-transparent`. */
export const STATE_SELECTED =
  'border-primary bg-primary/15 text-primary font-medium';

/** Hover state — shows primary border without background change. */
export const STATE_HOVER = 'hover:border-primary';

/** Disabled state used by all interactive controls. */
export const STATE_DISABLED =
  'disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50';

/** Explicit transition list — animates the colour-changing properties only,
 * never `transition-all`. */
export const SMOOTH_TRANSITION =
  'transition-[border-color,background-color,box-shadow,color] duration-150';

// ── Severity surface tokens ──────────────────────────────────────────────
// Shared colour palette for status-coloured containers (Card, Toast, etc.).
// Each severity defines opaque bg, border, text, and icon colours for
// light + dark mode. Components map their own variant names onto these.

export type SeverityLevel = 'success' | 'warning' | 'destructive' | 'info';

export interface SeveritySurface {
  /** Background colour. */
  bg: string;
  /** Ring colour — for components that use `ring-*` borders (e.g. Card). */
  ring: string;
  /** Border colour — for components that use `border-*` borders (e.g. Toast). */
  border: string;
  /** Primary text colour. */
  text: string;
  /** Secondary/muted text colour for descriptions and icons. */
  subtleText: string;
}

export const SEVERITY_SURFACE: Record<SeverityLevel, SeveritySurface> = {
  success: {
    bg: 'bg-success-100 dark:bg-success-900/40',
    ring: 'ring-success-300 dark:ring-success-700',
    border: 'border-success-300 dark:border-success-700',
    text: 'text-success-900 dark:text-success-100',
    subtleText: 'text-success-800 dark:text-success-200',
  },
  warning: {
    bg: 'bg-warning-100 dark:bg-warning-500/15',
    ring: 'ring-warning-300 dark:ring-warning-500/40',
    border: 'border-warning-300 dark:border-warning-500/40',
    text: 'text-warning-900 dark:text-warning-300',
    subtleText: 'text-warning-800 dark:text-warning-400',
  },
  destructive: {
    bg: 'bg-destructive/10 dark:bg-destructive/20',
    ring: 'ring-destructive/20 dark:ring-destructive/40',
    border: 'border-destructive/30 dark:border-destructive/40',
    text: 'text-destructive',
    subtleText: 'text-destructive/80 dark:text-red-400',
  },
  info: {
    bg: 'bg-primary-100 dark:bg-primary-900/40',
    ring: 'ring-primary-300 dark:ring-primary-700',
    border: 'border-primary-300 dark:border-primary-700',
    text: 'text-primary-900 dark:text-primary-100',
    subtleText: 'text-primary-800 dark:text-primary-200',
  },
};
